import * as PIXI from 'pixi.js';
import { useTokenStore } from '../stores/tokenStore';
import { LIGHT_PRESETS } from '@machina/core';

/**
 * TorchlightOverlay
 *
 * Canvas-based ambient darkness + light pools for the 2D battle map.
 * Same technique as IsoLightingSystem (MULTIPLY blend, radial gradient holes)
 * but using top-down world coordinates.
 *
 * Auto-activates when any token has `emitsLight`. When no light-emitting
 * tokens exist, the overlay is fully transparent (no effect).
 *
 * Added as a stage child alongside grid/tokens — pan/zoom handled by stage transform.
 */

export class TorchlightOverlay {
  public container: PIXI.Container;

  private overlay: PIXI.Sprite;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: PIXI.Texture;
  private ticker: PIXI.Ticker | null = null;
  private tickerFn: (() => void) | null = null;

  private worldWidth: number;
  private worldHeight: number;
  private gridSize: number;

  private ambientColor = { r: 15, g: 12, b: 8 };
  private ambientAlpha = 0.5;
  private flickerPhase = 0;

  /** Default light radius (in pixels) for characters near torches */
  private characterLightRadius = 80;

  constructor(gridSize: number, gridWidth: number, gridHeight: number) {
    this.container = new PIXI.Container();
    this.gridSize = gridSize;
    this.worldWidth = gridWidth * gridSize;
    this.worldHeight = gridHeight * gridSize;

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.worldWidth;
    this.canvas.height = this.worldHeight;
    this.ctx = this.canvas.getContext('2d')!;

    this.texture = PIXI.Texture.from(this.canvas);
    this.overlay = new PIXI.Sprite(this.texture);
    this.overlay.blendMode = PIXI.BLEND_MODES.MULTIPLY;
    this.container.addChild(this.overlay);
  }

  setTicker(ticker: PIXI.Ticker): void {
    this.ticker = ticker;
    this.tickerFn = () => {
      this.flickerPhase += 0.016;
      this.render();
    };
    ticker.add(this.tickerFn);
  }

  private render(): void {
    const ctx = this.ctx;
    const w = this.worldWidth;
    const h = this.worldHeight;

    const tokens = useTokenStore.getState().tokens;

    // Check if any token emits light — if not, clear overlay (fully transparent)
    let hasLightSource = false;
    for (const token of tokens.values()) {
      if (token.emitsLight && token.hpTier !== 'dead') {
        hasLightSource = true;
        break;
      }
    }

    if (!hasLightSource) {
      ctx.clearRect(0, 0, w, h);
      this.texture.update();
      return;
    }

    // Fill with dark ambient overlay
    const { r, g, b } = this.ambientColor;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${this.ambientAlpha})`;
    ctx.fillRect(0, 0, w, h);

    // Punch light holes using 'destination-out'
    ctx.globalCompositeOperation = 'destination-out';

    const feetPerSquare = 5;
    const pixelsPerFoot = this.gridSize / feetPerSquare;

    for (const token of tokens.values()) {
      if (token.hpTier === 'dead') continue;

      // Token center in world coords
      const cx = token.position.x + this.gridSize / 2;
      const cy = token.position.y + this.gridSize / 2;

      let radius: number;
      let flickerAmount: number;
      let hasWarmTint = false;

      if (token.emitsLight) {
        const preset = LIGHT_PRESETS[token.emitsLight];
        if (preset) {
          const totalFeet = preset.bright + preset.dim;
          radius = totalFeet * pixelsPerFoot;
          flickerAmount = preset.type === 'fire' ? 0.05 : 0.015;
          hasWarmTint = preset.type === 'fire';
        } else {
          radius = this.characterLightRadius;
          flickerAmount = 0.04;
        }
      } else {
        // Characters get a small ambient light when torches are present
        radius = this.characterLightRadius;
        flickerAmount = 0.02;
      }

      // Flicker
      const flicker = 1 + flickerAmount * Math.sin(this.flickerPhase * 2.2 + cx * 0.01)
                        + (flickerAmount * 0.5) * Math.sin(this.flickerPhase * 3.7 + cy * 0.01);
      const finalRadius = radius * flicker;

      // Punch a radial gradient hole
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, finalRadius);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(0.4, 'rgba(0, 0, 0, 0.8)');
      gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, finalRadius, 0, Math.PI * 2);
      ctx.fill();

      // Warm tint for fire sources
      if (hasWarmTint) {
        ctx.globalCompositeOperation = 'source-over';

        const warmGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, finalRadius);
        warmGrad.addColorStop(0, 'rgba(255, 140, 40, 0.06)');
        warmGrad.addColorStop(0.5, 'rgba(255, 100, 20, 0.03)');
        warmGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = warmGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, finalRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalCompositeOperation = 'destination-out';
      }
    }

    ctx.globalCompositeOperation = 'source-over';
    this.texture.update();
  }

  destroy(): void {
    if (this.ticker && this.tickerFn) {
      this.ticker.remove(this.tickerFn);
    }
    this.overlay.destroy();
    this.container.destroy({ children: true });
  }
}
